// Find My Part Search - Uses custom API to search products by vehicle
class FindMyPartSearch {
  constructor() {
    this.resultsGrid = document.getElementById('find-my-part-results-grid');
    this.loadingElement = document.getElementById('find-my-part-loading');
    this.errorElement = document.getElementById('find-my-part-error');
    this.noResultsElement = document.getElementById('find-my-part-no-results');
    this.resultsHeader = document.getElementById('find-my-part-results-header');
    this.resultsCount = document.getElementById('find-my-part-results-count');

    this.urlParams = new URLSearchParams(window.location.search);
    this.year = this.urlParams.get('year');
    this.make = this.urlParams.get('make');
    this.model = this.urlParams.get('model');
    this.partNumber = this.urlParams.get('number');
    this.vin = this.urlParams.get('vin');

    // Custom API endpoint
    this.apiBaseUrl = 'https://api.wixcodeexperts.com/api/parts/search';

    // Initialize card renderer and GraphQL fetcher
    this.cardRenderer = null;
    this.graphqlFetcher = null;

    this.init();
  }

  init() {
    // Initialize card renderer when available
    if (window.FindMyPartCard) {
      this.cardRenderer = new window.FindMyPartCard();
    } else {
      // Wait for card renderer to load
      document.addEventListener('DOMContentLoaded', () => {
        this.cardRenderer = new window.FindMyPartCard();
      });
    }

    // Initialize GraphQL fetcher
    if (window.FindMyPartGraphQL) {
      this.graphqlFetcher = new window.FindMyPartGraphQL();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        this.graphqlFetcher = new window.FindMyPartGraphQL();
      });
    }

    // Listen for filter events
    document.addEventListener('find-my-part:filters-applied', (e) => {
      this.renderFilteredProducts(e.detail.products, e.detail.count);
    });

    // Listen for car filter search event (triggered when user submits YMM selection)
    window.addEventListener('car-filter-search', (e) => {
      this.year = e.detail.year;
      this.make = e.detail.make;
      this.model = e.detail.model;
      this.vin = e.detail.vin || null; // Store VIN if provided
      this.partNumber = null; // Clear part number when searching by YMM

      // Update display text
      const displayElement = document.getElementById('ymm-display-text');
      if (displayElement) {
        if (this.vin) {
          displayElement.textContent = `${this.year} ${this.make} ${this.model} (VIN: ${this.vin})`;
        } else {
          displayElement.textContent = `${this.year} ${this.make} ${this.model}`;
        }
      }

      this.performSearch();
    });

    // Listen for part number search event
    window.addEventListener('part-number-search', (e) => {
      this.partNumber = e.detail.partNumber;
      this.year = null; // Clear YMM when searching by part number
      this.make = null;
      this.model = null;
      this.vin = null; // Clear VIN when searching by part number

      this.performSearch();
    });

    // Listen for VIN search event
    window.addEventListener('vin-search', (e) => {
      this.year = e.detail.year;
      this.make = e.detail.make;
      this.model = e.detail.model;
      this.vin = e.detail.vin;
      this.partNumber = null; // Clear part number when searching by VIN

      // Update display text
      const displayElement = document.getElementById('ymm-display-text');
      if (displayElement) {
        displayElement.textContent = `${this.year} ${this.make} ${this.model} (VIN: ${this.vin})`;
      }

      this.performSearch();
    });

    // If VIN provided in URL, decode it first
    if (this.vin && !this.year) {
      // VIN exists but no year/make/model - need to decode VIN
      this.decodeVINFromURL(this.vin);
    } else if ((this.year && this.make && this.model) || this.partNumber) {
      // If YMM selected or part number provided, automatically perform search
      this.performSearch();
    }
  }

  async decodeVINFromURL(vin) {
    // Show loading state
    this.showLoading();

    try {
      const vinApiUrl = 'https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVin';
      const url = `${vinApiUrl}/${vin}?format=json`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.Results && Array.isArray(data.Results)) {
        const results = data.Results;

        // Extract vehicle data
        const getField = (fieldName) => {
          const field = results.find((item) => item.Variable === fieldName);
          return field && field.Value ? field.Value : null;
        };

        const year = getField('Model Year');
        const make = getField('Make');
        const model = getField('Model');

        if (year && make && model) {
          // Store the decoded data
          this.year = year;
          this.make = make;
          this.model = model;

          // Update display text
          const displayElement = document.getElementById('ymm-display-text');
          if (displayElement) {
            displayElement.textContent = `${year} ${make} ${model} (VIN: ${vin})`;
          }

          // Perform search with decoded data
          this.performSearch();
        } else {
          console.error('[Search] Unable to decode VIN from URL');
          this.hideLoading();
          this.showError();
        }
      } else {
        throw new Error('Invalid VIN decoder response');
      }
    } catch (error) {
      console.error('[Search] Error decoding VIN from URL:', error);
      this.hideLoading();
      this.showError();
    }
  }

  async performSearch() {
    // Check if we have required parameters (either YMM or part number)
    if ((!this.year || !this.make || !this.model) && !this.partNumber) {
      console.error('Year/Make/Model or Part Number is required for search');
      this.showError();
      return;
    }

    this.showLoading();
    this.hideError();
    this.hideNoResults();
    this.hideResultsHeader();
    this.clearResults();

    try {
      const data = await this.searchProducts();

      if (data.success && data.products && data.products.length > 0) {
        this.displayResults(data.products, data.count, data.vehicle);
      } else {
        this.showNoResults();
      }
    } catch (error) {
      console.error('Search error:', error);
      this.showError();
    } finally {
      this.hideLoading();
    }
  }

  async searchProducts() {
    // Build API URL with query parameters
    const url = new URL(this.apiBaseUrl);

    // Add part number if provided
    if (this.partNumber) {
      url.searchParams.set('number', this.partNumber);
    } else {
      // Add YMM parameters
      url.searchParams.set('year', this.year);
      url.searchParams.set('make', this.make);
      url.searchParams.set('model', this.model);
    }

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Fetch error:', error);
      throw error;
    }
  }

  async displayResults(products, count, vehicle) {
    this.clearResults();

    // Show results count
    if (this.resultsCount) {
      this.resultsCount.textContent = `Found ${count} ${count === 1 ? 'part' : 'parts'}`;
    }
    this.showResultsHeader();

    // Scroll to results
    this.scrollToResults();

    // Dispatch event for filters to build their options
    document.dispatchEvent(
      new CustomEvent('find-my-part:products-loaded', {
        detail: {
          products: products,
          count: count,
          vehicle: vehicle,
        },
      })
    );

    // Ensure dependencies are initialized
    if (!this.cardRenderer) {
      this.cardRenderer = new window.FindMyPartCard();
    }
    if (!this.graphqlFetcher) {
      this.graphqlFetcher = new window.FindMyPartGraphQL();
    }

    // Extract all product GIDs for batch fetching
    const productGids = products.map((p) => p.shopify_id).filter((id) => id);

    // Batch fetch all products from Shopify using GraphQL
    const shopifyProductsMap = await this.graphqlFetcher.batchFetchProducts(productGids);

    // Render cards with API data first
    const cardElements = [];
    for (const apiProduct of products) {
      const productCard = this.cardRenderer.createCard(apiProduct);
      if (productCard) {
        this.resultsGrid.appendChild(productCard);
        cardElements.push({ element: productCard, apiProduct });
      }
    }

    // Update cards with Shopify data
    for (const { element, apiProduct } of cardElements) {
      const shopifyProduct = shopifyProductsMap[apiProduct.shopify_id];

      if (shopifyProduct) {
        this.cardRenderer.updateCardWithShopifyData(element, shopifyProduct);
      }
    }
  }

  async renderFilteredProducts(products, count) {
    this.clearResults();

    // Update results count
    if (this.resultsCount) {
      this.resultsCount.textContent = `Showing ${count} ${count === 1 ? 'part' : 'parts'}`;
    }
    this.showResultsHeader();

    // Scroll to results
    this.scrollToResults();

    if (products.length === 0) {
      this.showNoResults();
      return;
    }

    this.hideNoResults();

    // Ensure dependencies are initialized
    if (!this.cardRenderer) {
      this.cardRenderer = new window.FindMyPartCard();
    }
    if (!this.graphqlFetcher) {
      this.graphqlFetcher = new window.FindMyPartGraphQL();
    }

    // Extract all product GIDs for batch fetching
    const productGids = products.map((p) => p.shopify_id).filter((id) => id);

    // Batch fetch all products from Shopify using GraphQL
    const shopifyProductsMap = await this.graphqlFetcher.batchFetchProducts(productGids);

    // Render cards with API data first
    const cardElements = [];
    for (const apiProduct of products) {
      const productCard = this.cardRenderer.createCard(apiProduct);
      if (productCard) {
        this.resultsGrid.appendChild(productCard);
        cardElements.push({ element: productCard, apiProduct });
      }
    }

    // Update cards with Shopify data
    for (const { element, apiProduct } of cardElements) {
      const shopifyProduct = shopifyProductsMap[apiProduct.shopify_id];
      if (shopifyProduct) {
        this.cardRenderer.updateCardWithShopifyData(element, shopifyProduct);
      }
    }
  }

  showLoading() {
    this.loadingElement?.classList.add('active');
  }

  hideLoading() {
    this.loadingElement?.classList.remove('active');
  }

  showError() {
    this.errorElement?.classList.add('active');
  }

  hideError() {
    this.errorElement?.classList.remove('active');
  }

  showNoResults() {
    this.noResultsElement?.classList.add('active');
  }

  hideNoResults() {
    this.noResultsElement?.classList.remove('active');
  }

  showResultsHeader() {
    if (this.resultsHeader) {
      this.resultsHeader.style.display = 'block';
    }
  }

  hideResultsHeader() {
    if (this.resultsHeader) {
      this.resultsHeader.style.display = 'none';
    }
  }

  clearResults() {
    if (this.resultsGrid) {
      this.resultsGrid.innerHTML = '';
    }
  }

  scrollToResults() {
    const resultsSection = document.querySelector('.section-find-my-part');
    if (resultsSection) {
      // Small timeout to ensure DOM is updated before scrolling
      setTimeout(() => {
        // Get fixed header height
        const header = document.querySelector('.vsw-dragging-tray');
        const headerHeight = header ? header.offsetHeight : 0;

        // Get section position
        const sectionTop = resultsSection.getBoundingClientRect().top + window.pageYOffset;

        // Calculate target position with offset (header height)
        const targetPosition = sectionTop - headerHeight;

        // Scroll to position
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }, 100);
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new FindMyPartSearch();
  });
} else {
  new FindMyPartSearch();
}
